import { MessageMedium } from 'constants/messageMediums';
import { TimespanEnum } from 'modules/intelligentInsights/types';
import { ApolloError } from '@apollo/client';

export enum STOMessageMediumTabs {
  Email = MessageMedium.Email,
  Push = MessageMedium.Push,
  SMS = MessageMedium.SMS,
}

export type STOMessageMediumTab = keyof typeof STOMessageMediumTabs;

// Send Time Optimization Response Types
export interface SendTimeOptimizationInsights {
  startDate: string;
  endDate: string;
  userLocalTimeAnalytics: TimeAnalytics;
  projectTimeAnalytics: TimeAnalytics;
}

export interface TimeAnalytics {
  overallEngagement: EngagementComparison;
  weeklyDistribution: WeeklyDistribution[];
  mostPreferredSendWindow: string;
  leastPreferredSendWindow: string;
  channelData: ChannelData;
}

export interface EngagementComparison {
  control: SendTimeEngagementMetrics;
  treatment: SendTimeEngagementMetrics;
  lift: EngagementLift;
}

export interface SendTimeEngagementMetrics {
  clickRate: number;
  openRate: number;
  unsubscribeRate: number;
}

export interface EngagementLift {
  clickRateLift: number;
  openRateLift: number;
  unsubscribeRateLift: number;
}

export interface WeeklyDistribution {
  weekStartDate: string;
  weekday: TimeWindowDistribution;
  weekend: TimeWindowDistribution;
}

export interface TimeWindowDistribution {
  Morning: number;
  Afternoon: number;
  Evening: number;
  Night: number;
}

export interface ChannelData {
  [channel: string]: ChannelAnalytics;
}

export interface TopPerformingCampaign {
  campaignId: string;
  campaignName: string;
  engagement: EngagementComparison;
  totalSends: number;
  percentageSendsStoEnabled: number;
}

export interface ChannelAnalytics {
  stoEnabledPercentage: number;
  optimizedUsersCount: number;
  percentOfUsersWithOptimizedSend: number;
  totalOptimizedSends: number;
  sendsByDayAndTime: SendsByDayAndTime;
  topSendWindows: string[];
  engagementMetrics: EngagementComparison;
  deliveryMetrics: DeliveryMetrics;
  topPerformingCampaigns: TopPerformingCampaign[];
}

export interface SendsByDayAndTime {
  monday: DayTimeDistribution;
  tuesday: DayTimeDistribution;
  wednesday: DayTimeDistribution;
  thursday: DayTimeDistribution;
  friday: DayTimeDistribution;
  saturday: DayTimeDistribution;
  sunday: DayTimeDistribution;
}

export interface DayTimeDistribution {
  morning: number;
  afternoon: number;
  evening: number;
  night: number;
}

export interface DeliveryMetrics {
  control: DeliveryMetricsData;
  treatment: DeliveryMetricsData;
  sendSkips: SendSkipBreakdown;
}

export interface DeliveryMetricsData {
  bounceRate: number;
  deliveryRate: number;
  sendSkipRate: number;
  sendSkips: number;
  bounces: number;
}

export interface SendSkipBreakdown {
  userPreference: number;
  quietHours: number;
  frequencyOptimize: number;
  technicalError: number;
}

/**
  Context types
 */
export interface STOInsightsCoreContext {
  fetchCoreData: (nextTimespan: TimespanEnum) => void;
  insightsData: SendTimeOptimizationInsights | null;
  insightsErrorMessage: ApolloError | null;
  isError: boolean;
  isLoading: boolean;
  isReady: boolean;
  selectedMedium: STOMessageMediumTabs;
  setSelectedMedium: (medium: STOMessageMediumTabs) => void;
}

export type STOInsightsCoreContextVars = {
  timespan: TimespanEnum;
  startDate: string;
  endDate: string;
};
